import { Helmet } from "react-helmet";

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "blog";
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  noindex?: boolean;
}

const defaultSEO = {
  siteName: "SpotlightAdz",
  title: "SpotlightAdz - Morocco's #1 Digital Marketing Agency",
  description: "SpotlightAdz is Morocco's leading digital marketing agency. Expert services in SEO, social media marketing, paid advertising, content creation, and web development. Grow your business with data-driven strategies.",
  keywords: "digital marketing agency Morocco, SEO Morocco, social media marketing Casablanca, paid advertising Morocco, web development Morocco, content marketing, brand strategy, SpotlightAdz, agence marketing digital Maroc, marketing numérique",
  siteUrl: "https://spotlightadz.ma",
  ogImage: "https://spotlightadz.ma/logo.png",
  twitterHandle: "@spotlightadz",
  locale: "en_US",
  alternateLocales: ["fr_MA", "ar_MA"],
};

export const SEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = "website",
  author,
  publishedTime,
  modifiedTime,
  section,
  tags,
  noindex = false,
}: SEOHeadProps) => {
  const fullTitle = title ? `${title} | ${defaultSEO.siteName}` : defaultSEO.title;
  const metaDescription = description || defaultSEO.description;
  const metaKeywords = keywords || defaultSEO.keywords;
  const canonical = canonicalUrl || defaultSEO.siteUrl;
  const image = ogImage || defaultSEO.ogImage;

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://spotlightadz.ma/#organization",
    name: "SpotlightAdz",
    alternateName: ["Spotlight Adz", "SpotlightAdz Morocco", "SpotlightAdz Agency"],
    url: "https://spotlightadz.ma",
    logo: {
      "@type": "ImageObject",
      url: "https://spotlightadz.ma/logo.png",
      width: 512,
      height: 512,
    },
    sameAs: [
      "https://spotlightadz.com",
      "https://facebook.com/spotlightadz",
      "https://instagram.com/spotlightadz",
      "https://linkedin.com/company/spotlightadz",
      "https://twitter.com/spotlightadz",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+212-XXX-XXXXXX",
      contactType: "customer service",
      areaServed: ["MA", "WORLD"],
      availableLanguage: ["English", "French", "Arabic"],
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "MA",
      addressLocality: "Morocco",
    },
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://spotlightadz.ma/#localbusiness",
    name: "SpotlightAdz - Digital Marketing Agency",
    image: "https://spotlightadz.ma/logo.png",
    url: "https://spotlightadz.ma",
    telephone: "+212-XXX-XXXXXX",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressCountry: "Morocco",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 33.5731,
      longitude: -7.5898,
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "150",
    },
  };

  // WebSite Schema with SearchAction
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://spotlightadz.ma/#website",
    url: "https://spotlightadz.ma",
    name: "SpotlightAdz",
    description: defaultSEO.description,
    publisher: {
      "@id": "https://spotlightadz.ma/#organization",
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://spotlightadz.ma/blog?search={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
    inLanguage: ["en", "fr", "ar"],
  };

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Digital Marketing",
    provider: {
      "@id": "https://spotlightadz.ma/#organization",
    },
    areaServed: {
      "@type": "Country",
      name: "Morocco",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Digital Marketing Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Social Media Marketing",
            description: "Strategic social media management and advertising",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "SEO Optimization",
            description: "Search engine optimization for better rankings",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Paid Advertising",
            description: "PPC campaigns on Google and social platforms",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Content Creation",
            description: "Professional content and copywriting services",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Web Development",
            description: "Custom website design and development",
          },
        },
      ],
    },
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={author || "SpotlightAdz"} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="bingbot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Alternate Languages */}
      <link rel="alternate" hrefLang="en" href="https://spotlightadz.com" />
      <link rel="alternate" hrefLang="fr" href="https://spotlightadz.ma" />
      <link rel="alternate" hrefLang="ar" href="https://spotlightadz.ma" />
      <link rel="alternate" hrefLang="x-default" href="https://spotlightadz.ma" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={defaultSEO.locale} />
      {defaultSEO.alternateLocales.map((locale) => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />
      <meta name="twitter:creator" content={defaultSEO.twitterHandle} />

      {/* Article specific (for blog posts) */}
      {ogType === "article" && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {author && <meta property="article:author" content={author} />}
          {section && <meta property="article:section" content={section} />}
          {tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#FF6B35" />
      <meta name="msapplication-TileColor" content="#FF6B35" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="geo.region" content="MA" />
      <meta name="geo.placename" content="Morocco" />
      <meta name="rating" content="general" />
      <meta name="referrer" content="origin-when-cross-origin" />
      
      {/* Verification Tags (add your actual verification codes) */}
      <meta name="google-site-verification" content="YOUR_GOOGLE_VERIFICATION_CODE" />
      <meta name="msvalidate.01" content="YOUR_BING_VERIFICATION_CODE" />
      <meta name="yandex-verification" content="YOUR_YANDEX_VERIFICATION_CODE" />

      {/* Preconnect to important domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
